import React, { useState, useCallback, useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Grid from '@mui/material/Grid';
import { useSearchParams } from 'react-router-dom';
import { useAsync, useAsyncRetry } from 'react-use';
import { useApi } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { DashboardHeader } from './DashboardHeader';
import { KeysTable } from './KeysTable';
import { GenerateKeyDialog } from './GenerateKeyDialog';
import { ManageTeamDialog } from './ManageTeamDialog';
import { UsageStats } from './UsageStats';
import { LiteLLMBudgetWidget } from './LiteLLMBudgetWidget';
import { TeamUsage } from './TeamUsage';
import { ModelsTable } from './ModelsTable';
import { AuditLog } from './AuditLog';
import { liteLlmApiRef } from '../api';
import {
  litellmTeamCreatePermission,
  litellmTeamManagePermission,
  litellmTeamMembersManagePermission,
  litellmTeamKnowledgebaseManagePermission,
  litellmTeamMcpManagePermission,
} from '../permissions';
import { DateRange, GenerateKeyRequest, GenerateKeyResponse, UpdateKeyRequest, UsageMetrics, CreateTeamRequest, UpdateTeamRequest, TeamInfo } from '../types';

const PERIOD_LS_KEY = 'litellm_usage_period';
type DatePreset = 'today' | '24h' | '7d' | '30d';

type PageTab = 'overview' | 'keys' | 'teams' | 'models' | 'audit';
const PAGE_TABS: readonly PageTab[] = ['overview', 'keys', 'teams', 'models', 'audit'];
const isPageTab = (v: string | null): v is PageTab =>
  !!v && (PAGE_TABS as readonly string[]).includes(v);

function initDateRange(): DateRange {
  let preset = '7d';
  try { preset = localStorage.getItem(PERIOD_LS_KEY) ?? '7d'; } catch { /* ignore */ }
  const end = new Date();
  const start = new Date();
  if (preset === 'today') start.setHours(0, 0, 0, 0);
  else if (preset === '24h') start.setHours(start.getHours() - 24);
  else if (preset === '30d') start.setDate(start.getDate() - 30);
  else start.setDate(start.getDate() - 7);
  return { start, end };
}

export const LiteLLMPage: React.FC = () => {
  const api = useApi(liteLlmApiRef);

  const [dateRange, setDateRange] = useState<DateRange>(initDateRange);
  const [currentPreset, setCurrentPreset] = useState<DatePreset>(() => {
    try { return (localStorage.getItem(PERIOD_LS_KEY) as DatePreset) ?? '7d'; } catch { return '7d'; }
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<PageTab>(() => {
    const t = searchParams.get('tab');
    return isPageTab(t) ? t : 'overview';
  });

  // `?generate=1` (e.g. from a homepage budget card's "New key" CTA) opens the
  // generate-key dialog on arrival. The param is cleared once honoured so a
  // manual close doesn't immediately reopen it on the next render.
  const [generateDialogOpen, setGenerateDialogOpen] = useState(
    () => searchParams.get('generate') === '1',
  );
  useEffect(() => {
    if (searchParams.get('generate') !== '1') return;
    setGenerateDialogOpen(true);
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        next.delete('generate');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams]);

  // Keep `?tab=` in sync so links (e.g. the budget widget's "more keys"
  // note pointing at `/litellm?tab=keys`) land on the right tab and the URL
  // stays shareable.
  const selectTab = useCallback(
    (tab: PageTab) => {
      setActiveTab(tab);
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.set('tab', tab);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'warning' | 'error' } | null>(null);
  const [manageTeam, setManageTeam] = useState<{ mode: 'create' | 'edit'; team?: TeamInfo } | null>(null);

  // Team usage cache: teamId -> UsageMetrics
  const [teamUsageCache, setTeamUsageCache] = useState<Record<string, UsageMetrics | null>>({});
  const [teamUsageLoading, setTeamUsageLoading] = useState<Record<string, boolean>>({});

  const { value: userInfo, loading: userLoading, error: userError } = useAsync(
    () => api.getUserInfo(),
    [api],
  );

  const { value: keys, loading: keysLoading, retry: refreshKeys } = useAsyncRetry(
    async () => {
      try {
        return await api.listKeys();
      } catch (e: any) {
        setSnackbar({ message: `Failed to load keys: ${e.message}`, severity: 'error' });
        return [];
      }
    },
    [api],
  );

  const { value: allModels, loading: modelsLoading } = useAsync(
    () => api.listModels().catch(() => []),
    [api],
  );

  const { value: allTeams, loading: teamsLoading, retry: refreshTeams } = useAsyncRetry(
    () => api.getTeams().catch(() => []),
    [api],
  );

  const { value: liteLlmConfig } = useAsync(() => api.getConfig(), [api]);

  // Teams the caller administers by group ownership (not membership). Only
  // fetched when team management is enabled; failures degrade to an empty list.
  const { value: managedTeams } = useAsync(
    async () =>
      liteLlmConfig?.teamManagement?.enabled
        ? api.getManagedTeams().catch(() => [])
        : [],
    [api, liteLlmConfig],
  );

  const { allowed: canCreateTeam } = usePermission({ permission: litellmTeamCreatePermission });
  const { allowed: canManageTeam } = usePermission({ permission: litellmTeamManagePermission });
  const { allowed: canManageMembers } = usePermission({ permission: litellmTeamMembersManagePermission });
  const { allowed: canManageKnowledgeBases } = usePermission({ permission: litellmTeamKnowledgebaseManagePermission });
  const { allowed: canManageMcpServers } = usePermission({ permission: litellmTeamMcpManagePermission });
  const teamMgmtEnabled = liteLlmConfig?.teamManagement?.enabled ?? false;
  const objectPermsEnabled = liteLlmConfig?.teamManagement?.objectPermissionsEnabled ?? false;

  const { value: vectorStores } = useAsync(
    async () =>
      objectPermsEnabled ? api.getVectorStores().catch(() => []) : [],
    [api, objectPermsEnabled],
  );
  const { value: mcpServers } = useAsync(
    async () =>
      objectPermsEnabled ? api.getMcpServers().catch(() => []) : [],
    [api, objectPermsEnabled],
  );

  // Filter to teams the current user belongs to
  const teams = useMemo(() => {
    if (!allTeams?.length) return [];
    if (!userInfo) return allTeams;
    const userId = userInfo.user_id;
    if (userInfo.teams?.length) {
      return allTeams.filter(t => userInfo.teams!.includes(t.team_id));
    }
    const byMembership = allTeams.filter(t =>
      t.members_with_roles?.some(m => m.user_id === userId),
    );
    return byMembership.length > 0 ? byMembership : allTeams;
  }, [allTeams, userInfo]);

  const { value: usage, loading: usageLoading } = useAsync(async () => {
    const startDate = dateRange.start.toISOString().split('T')[0];
    const endDate = dateRange.end.toISOString().split('T')[0];
    return api.getUsage(startDate, endDate);
  }, [api, dateRange]);

  // Invalidate team usage cache when date range changes so stale data isn't shown
  const handleDateRangeChange = useCallback((range: DateRange, preset?: DatePreset) => {
    setDateRange(range);
    if (preset) {
      setCurrentPreset(preset);
    }
    setTeamUsageCache({});
    setTeamUsageLoading({});
  }, [setDateRange, setCurrentPreset]);

  // Fetch team usage on demand when a team card is expanded
  const loadTeamUsage = useCallback(async (teamId: string) => {
    if (teamUsageCache[teamId] !== undefined || teamUsageLoading[teamId]) return;
    setTeamUsageLoading(prev => ({ ...prev, [teamId]: true }));
    try {
      const startDate = dateRange.start.toISOString().split('T')[0];
      const endDate = dateRange.end.toISOString().split('T')[0];
      const data = await api.getTeamUsage(teamId, startDate, endDate);
      setTeamUsageCache(prev => ({ ...prev, [teamId]: data }));
    } catch {
      setTeamUsageCache(prev => ({ ...prev, [teamId]: null }));
    } finally {
      setTeamUsageLoading(prev => ({ ...prev, [teamId]: false }));
    }
  }, [api, dateRange, teamUsageCache, teamUsageLoading]);

  // Models available for key generation: intersection of all models with user-level
  // and team-level restrictions. If a user has no model restrictions, all models are allowed.
  const allowedModels = useMemo(() => {
    if (!allModels?.length) return [];
    const userModels = userInfo?.models;
    const teamModels = teams?.flatMap(t => t.models ?? []);
    const hasUserRestriction = userModels && userModels.length > 0;
    const hasTeamRestriction = teamModels && teamModels.length > 0;
    if (!hasUserRestriction && !hasTeamRestriction) return allModels;
    const allowed = new Set([
      ...(hasUserRestriction ? userModels! : allModels.map(m => m.model_name)),
      ...(hasTeamRestriction ? teamModels! : []),
    ]);
    return allModels.filter(m => allowed.has(m.model_name));
  }, [allModels, userInfo, teams]);

  const handleGenerateKey = useCallback(
    async (request: GenerateKeyRequest): Promise<GenerateKeyResponse> => {
      try {
        const response = await api.generateKey(request);
        setSnackbar({ message: 'Key generated successfully', severity: 'success' });
        refreshKeys();
        return response;
      } catch (e: any) {
        setSnackbar({ message: `Failed to generate key: ${e.message}`, severity: 'error' });
        throw e;
      }
    },
    [api, refreshKeys],
  );

  const handleUpdateKey = useCallback(
    async (keyId: string, request: UpdateKeyRequest) => {
      try {
        await api.updateKey(keyId, request);
        setSnackbar({ message: 'Key updated successfully', severity: 'success' });
        refreshKeys();
      } catch (e: any) {
        setSnackbar({ message: `Failed to update key: ${e.message}`, severity: 'error' });
        throw e;
      }
    },
    [api, refreshKeys],
  );

  const handleBlockKey = useCallback(
    async (keyId: string) => {
      try {
        await api.blockKey(keyId);
        setSnackbar({ message: 'Key blocked — requests will be rejected until unblocked', severity: 'warning' });
        refreshKeys();
      } catch (e: any) {
        setSnackbar({ message: `Failed to block key: ${e.message}`, severity: 'error' });
      }
    },
    [api, refreshKeys],
  );

  const handleUnblockKey = useCallback(
    async (keyId: string) => {
      try {
        await api.unblockKey(keyId);
        setSnackbar({ message: 'Key unblocked', severity: 'success' });
        refreshKeys();
      } catch (e: any) {
        setSnackbar({ message: `Failed to unblock key: ${e.message}`, severity: 'error' });
      }
    },
    [api, refreshKeys],
  );

  const handleResetKeySpend = useCallback(
    async (keyId: string) => {
      try {
        await api.resetKeySpend(keyId);
        setSnackbar({ message: 'Spend counter reset to $0', severity: 'success' });
        refreshKeys();
      } catch (e: any) {
        setSnackbar({ message: `Failed to reset spend: ${e.message}`, severity: 'error' });
      }
    },
    [api, refreshKeys],
  );

  const handleDeleteKey = useCallback(
    async (keyId: string) => {
      try {
        await api.deleteKey(keyId);
        setSnackbar({ message: 'Key revoked successfully', severity: 'success' });
        refreshKeys();
      } catch (e: any) {
        if (e.body?.success && (e.body?.message?.includes('already deleted') || e.body?.message?.includes('never existed'))) {
          setSnackbar({ message: 'Key was already deleted', severity: 'warning' });
          refreshKeys();
          return;
        }
        setSnackbar({ message: `Failed to revoke key: ${e.message}`, severity: 'error' });
      }
    },
    [api, refreshKeys],
  );

  const handlePruneExpiredKeys = useCallback(
    async () => {
      try {
        const result = await api.pruneExpiredKeys();
        setSnackbar({ message: `Pruned ${result.pruned} expired key${result.pruned !== 1 ? 's' : ''}`, severity: 'success' });
        refreshKeys();
      } catch (e: any) {
        setSnackbar({ message: `Failed to prune expired keys: ${e.message}`, severity: 'error' });
      }
      return { pruned: 0 };
    },
    [api, refreshKeys],
  ) as () => Promise<{ pruned: number }>;

  const isInitialLoading = userLoading && !userInfo;

  if (isInitialLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  // User exists in Backstage but has no LiteLLM account
  if (userError || !userInfo) {
    const isProvisioningEnabled = (userError as any)?.body?.provisioning === true;
    const hint = (userError as any)?.body?.hint;
    return (
      <Box p={3}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Account not provisioned</Typography>
          <Typography color="text.secondary" paragraph>
            Your Backstage account is not linked to a LiteLLM user.
          </Typography>
          {hint ? (
            <Typography variant="body2" color="text.secondary">{hint}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              {isProvisioningEnabled
                ? 'Auto-provisioning is enabled but failed. Check the backend logs.'
                : 'Set litellm.provisioning.enabled: true in app-config.yaml to enable auto-provisioning, or ask your administrator to create the account manually.'}
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  const pageTabs = (
    <Tabs
      value={activeTab}
      onChange={(_, v) => selectTab(v as PageTab)}
      variant="scrollable"
      scrollButtons="auto"
      // Substring class matching so these survive a host MUI classname prefix
      // (Backstage renders `v5-MuiTabs-indicator`, not `MuiTabs-indicator`).
      sx={{
        minHeight: 44,
        '& [class*="MuiTabs-indicator"]': { height: 2, borderRadius: '2px 2px 0 0' },
        '& [class*="MuiTab-root"]': { minHeight: 44, textTransform: 'none', fontSize: 14 },
      }}
    >
      <Tab label="Overview" value="overview" />
      <Tab label="Keys" value="keys" />
      <Tab label="Teams" value="teams" />
      <Tab label="Models" value="models" />
      {userInfo.can_view_audit && <Tab label="Audit Log" value="audit" />}
    </Tabs>
  );

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <DashboardHeader
        userInfo={userInfo}
        teams={teams ?? []}
        keys={keys ?? []}
        loading={userLoading || teamsLoading}
        onGenerateKeyClick={() => setGenerateDialogOpen(true)}
        tabs={pageTabs}
      />

      {activeTab === 'overview' && (
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} lg={8}>
            <UsageStats
              usage={usage ?? null}
              models={allModels ?? []}
              dateRange={dateRange}
              currentPreset={currentPreset}
              onDateRangeChange={handleDateRangeChange}
              loading={usageLoading}
              userInfo={userInfo}
            />
          </Grid>
          <Grid item xs={12} lg={4}>
            <LiteLLMBudgetWidget compact collapsible />
          </Grid>
        </Grid>
      )}

      {activeTab === 'keys' && (
        <KeysTable
          keys={keys ?? []}
          models={allowedModels}
          loading={keysLoading || modelsLoading}
          onGenerateKeyClick={() => setGenerateDialogOpen(true)}
          onUpdateKey={handleUpdateKey}
          onBlockKey={handleBlockKey}
          onUnblockKey={handleUnblockKey}
          onResetKeySpend={handleResetKeySpend}
          onDeleteKey={handleDeleteKey}
          onPruneExpiredKeys={handlePruneExpiredKeys}
        />
      )}

      {activeTab === 'teams' && (() => {
        // Union of teams the user is a member of and teams their group owns,
        // de-duplicated by team_id (membership entries win on conflict).
        const teamsById = new Map<string, TeamInfo>();
        for (const t of managedTeams ?? []) teamsById.set(t.team_id, t);
        for (const t of teams ?? []) teamsById.set(t.team_id, t);
        const visibleTeams = Array.from(teamsById.values());
        return (
          <TeamUsage
            teams={visibleTeams}
            loading={teamsLoading}
            getTeamUsage={teamId => {
              if (teamUsageCache[teamId] === undefined) loadTeamUsage(teamId);
              return teamUsageCache[teamId] ?? null;
            }}
            getTeamUsageLoading={teamId => teamUsageLoading[teamId] ?? false}
            canManage={teamMgmtEnabled && canManageTeam}
            onEditTeam={t => setManageTeam({ mode: 'edit', team: t })}
            canCreate={teamMgmtEnabled && canCreateTeam}
            onCreateTeam={() => setManageTeam({ mode: 'create' })}
          />
        );
      })()}

      {activeTab === 'models' && (
        <ModelsTable
          allModels={allModels ?? []}
          teams={teams ?? []}
          loading={modelsLoading}
        />
      )}

      {activeTab === 'audit' && userInfo.can_view_audit && <AuditLog api={api} />}

      <GenerateKeyDialog
        open={generateDialogOpen}
        onClose={() => setGenerateDialogOpen(false)}
        keys={keys ?? []}
        models={allowedModels}
        teams={teams ?? []}
        username={userInfo.user_id}
        keyGenerationSettings={liteLlmConfig?.keyGeneration}
        onGenerateKey={handleGenerateKey}
        onGetConfig={() => api.getConfig()}
      />

      <ManageTeamDialog
        open={!!manageTeam}
        onClose={() => setManageTeam(null)}
        mode={manageTeam?.mode ?? 'create'}
        team={manageTeam?.team}
        allModels={allModels ?? []}
        config={liteLlmConfig}
        onSubmit={async payload => {
          if (manageTeam?.mode === 'edit' && manageTeam.team) {
            await api.updateTeam(manageTeam.team.team_id, payload as UpdateTeamRequest);
          } else {
            await api.createTeam(payload as CreateTeamRequest);
          }
          setSnackbar({ message: 'Team saved', severity: 'success' });
          refreshTeams();
        }}
        canManageMembers={teamMgmtEnabled && canManageMembers}
        onAddMember={async (userEntityRef, maxBudgetInTeam) => {
          if (!manageTeam?.team) return;
          const updated = await api.addTeamMember(manageTeam.team.team_id, {
            userEntityRef,
            ...(maxBudgetInTeam ? { maxBudgetInTeam } : {}),
          });
          setManageTeam(s => (s && s.team ? { ...s, team: updated } : s));
          refreshTeams();
        }}
        onRemoveMember={async userEntityRef => {
          if (!manageTeam?.team) return;
          const updated = await api.removeTeamMember(
            manageTeam.team.team_id,
            userEntityRef,
          );
          setManageTeam(s => (s && s.team ? { ...s, team: updated } : s));
          refreshTeams();
        }}
        canManageKnowledgeBases={
          teamMgmtEnabled && objectPermsEnabled && canManageKnowledgeBases
        }
        vectorStores={vectorStores ?? []}
        onSaveKnowledgeBases={async vectorStoreIds => {
          if (!manageTeam?.team) return;
          const updated = await api.setTeamKnowledgeBases(
            manageTeam.team.team_id,
            vectorStoreIds,
          );
          setManageTeam(s => (s && s.team ? { ...s, team: updated } : s));
          setSnackbar({ message: 'Knowledge bases updated', severity: 'success' });
          refreshTeams();
        }}
        canManageMcpServers={
          teamMgmtEnabled && objectPermsEnabled && canManageMcpServers
        }
        mcpServers={mcpServers ?? []}
        onSaveMcpServers={async mcpServerIds => {
          if (!manageTeam?.team) return;
          const updated = await api.setTeamMcpServers(
            manageTeam.team.team_id,
            mcpServerIds,
          );
          setManageTeam(s => (s && s.team ? { ...s, team: updated } : s));
          setSnackbar({ message: 'MCP servers updated', severity: 'success' });
          refreshTeams();
        }}
      />

      <Snackbar
        open={!!snackbar}
        autoHideDuration={5000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};
